/* eslint-disable spire/export-styles */
import AwesomePhone from "@cahaba/XDL/Icons/AwesomePhone";
import AwesomeStore from "@cahaba/XDL/Icons/AwesomeStore";
import MapMarkerWhite from "@cahaba/XDL/Icons/MapMarkerWhite";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { costCodesStyles } from "@insite/content-library/Widgets/Budget/CostCodes";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import MobiusLink from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.icon]: keyof typeof iconMap;
    };
}

const iconMap = {
    location: MapMarkerWhite,
    contactUs: AwesomePhone,
    store: AwesomeStore,
    none: undefined,
} as const;

const linkStyles: InjectableCss = {
    css: css`
        display: flex;
        align-items: center;
        > span {
            display: flex;
            align-items: center;
        }
    `,
};

export const LinkWithIcon: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);
    const iconSrc = iconMap[fields.icon || "none"];
    const iconProps: IconPresentationProps = {
        src: iconSrc,
        color: "white",
        size: 14,
        css: css`
        margin-right: 10px;`,
    }

    const icon = <Icon {...iconProps} />

    const styles = useMergeStyles("link", linkStyles);

    if (!url) {
        return null;
    }

    return (
        <StyledWrapper style={{display: "flex", height: "100%", alignItems: "center"}}>
            <MobiusLink {...styles} href={url}>
                {icon}
                <Typography as="p" transform="uppercase" color="white" weight="normal" size={14}>{fields.overrideTitle || title || url}</Typography>
            </MobiusLink>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: LinkWithIcon,
    definition: {
        group: "Basic",
        icon: "Link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.icon,
                displayName: "Display Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Location",
                        value: "location",
                    },
                    {
                        displayName: "Contact Us",
                        value: "contactUs"
                    },
                    {
                        displayName: "Store",
                        value: "store",
                    },
                ],
                defaultValue: "MapMarkerWhite",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
